import { EChartsOption } from 'echarts';
import { DataType, TableFeatureResponse } from '../models/api-responses';

export function histogramChartOptions(
  feature: TableFeatureResponse,
  barWidth: string = '100%',
): EChartsOption {
  const valueCounts = JSON.parse(feature.profile.valueCounts ?? '[]');
  valueCounts.sort((a, b) => a.value - b.value);
  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
    },
    xAxis: {
      type: 'category',
      data: valueCounts.map((item) => item.value),
      axisLine: { show: true },
      axisTick: { show: true },
      axisLabel: {
        interval: 10,
        rotate: 45,
      },
    },
    yAxis: {
      type: 'value',
      splitLine: { show: true },
    },
    series: [
      {
        type: 'bar',
        data: valueCounts.map((item) => item.count),
        barWidth,
      },
    ],
  };
}

export function correlationBarChartOptions(
  feature: TableFeatureResponse,
): EChartsOption {
  const correlationMatrix = feature.profile.correlationMatrix ?? {};
  const keys = Object.keys(correlationMatrix);
  const values = keys.map((key: string) => correlationMatrix[key]);

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
    },
    xAxis: {
      type: 'category',
      data: keys,
      axisLine: { show: true },
      axisTick: { show: true },
      axisLabel: {
        rotate: 45,
      },
    },
    yAxis: {
      type: 'value',
      splitLine: { show: true },
    },
    series: [
      {
        type: 'bar',
        data: values,
      },
    ],
  };
}

export function dateHistogramChartOptions(
  feature: TableFeatureResponse,
): EChartsOption {
  const valueCounts = JSON.parse(feature.profile.valueCounts ?? '[]');
  valueCounts.sort((a, b) => a.value - b.value);
  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
    },
    xAxis: {
      type: 'time',
      axisLine: { show: true },
      axisTick: { show: true },
    },
    yAxis: {
      type: 'value',
      splitLine: { show: true },
    },
    series: [
      {
        type: 'bar',
        data: valueCounts.map((item) => [
          new Date(item.value).getTime(),
          item.count,
        ]),
        barWidth: '60%',
        itemStyle: {
          color: '#6E7DFF',
        },
      },
    ],
  };
}

export function pieChartOptions(feature: TableFeatureResponse): EChartsOption {
  const valueCounts = JSON.parse(feature.profile.valueCounts ?? '[]');
  return {
    tooltip: {
      trigger: 'item',
      formatter: '{b}: {d}%',
      position: 'top',
    },
    series: [
      {
        type: 'pie',
        radius: '55%',
        center: ['50%', '35%'],
        data: valueCounts.map((datum) => {
          return {
            value: datum.count,
            name:
              feature.dataType === DataType.Bool
                ? (datum.value?.toString() ?? 'null')
                : (datum.value?.label?.toString() ?? 'null'),
          };
        }),
        label: {
          show: true,
          formatter: '{d}%',
          position: 'outside',
          fontSize: 14,
          color: '#000',
        },
      },
    ],
    legend: {
      orient: 'horizontal',
      bottom: 0,
      left: 'center',
      itemWidth: 10,
      itemHeight: 10,
      icon: 'circle',
      textStyle: {
        fontSize: 14,
        color: '#000000',
      },
    },
  };
}

export function gaugeChartOptions(
  feature: TableFeatureResponse,
  data: GaugeValue[],
): EChartsOption {
  return {
    series: [
      {
        type: 'gauge',
        min: feature.minValue ?? 0,
        max: feature.maxValue ?? Math.round(data[data.length - 1].value * 1.2),
        anchor: {
          show: true,
          showAbove: true,
          size: 18,
          itemStyle: {
            color: '#FAC858',
          },
        },
        pointer: {
          icon: 'path://M2.9,0.7L2.9,0.7c1.4,0,2.6,1.2,2.6,2.6v115c0,1.4-1.2,2.6-2.6,2.6l0,0c-1.4,0-2.6-1.2-2.6-2.6V3.3C0.3,1.9,1.4,0.7,2.9,0.7z',
          width: 8,
          length: '80%',
          offsetCenter: [0, '8%'],
        },
        progress: {
          show: true,
          overlap: true,
          roundCap: true,
        },
        axisLine: {
          roundCap: true,
        },
        data,
        title: {
          fontSize: 14,
        },
        detail: {
          width: 80,
          height: 14,
          fontSize: 12,
          color: '#fff',
          padding: [0, 8, 0, 8],
          backgroundColor: 'inherit',
          borderRadius: 3,
          formatter: '{value}',
        },
      },
    ],
  };
}

export interface GaugeValue {
  value: number;
  name: string;
  itemStyle: {
    color: string;
  };
  title: {
    offsetCenter: string[];
  };
  detail: {
    offsetCenter: string[];
  };
}
